import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  IconButton,
  Grid,
  Paper,
  Chip,
  Alert,
  Collapse,
  Card,
  CardContent,
  Tooltip,
  Divider,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import SaveIcon from "@mui/icons-material/Save";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PageTitle from "../../../widgets/PageTitle";
import { API_ENDPOINT } from "../../../utils/constants";
import { getHeader, isValidUrl, toastError } from "../../../utils/util";
import axios from "axios";
import { toast } from "react-toastify";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { materialDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import SaveButton from "./SaveButton";

const EndpointMessage = ({
  value = { url: "" },
  isEdit = false,
  editId,
  preview = false,
  onUpdate,
}) => {
  const [body, setBody] = useState(value?.url ?? "");
  const [reply, setReply] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [openGuide, setOpenGuide] = useState(false);
  const [copiedCurl, setCopiedCurl] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState("919876543210");
  const [profileName, setProfileName] = useState("ACME Demo");
  const [keyword, setKeyword] = useState("hello");
  const [optionalFields, setOptionalFields] = useState([
    { key: "", value: "" },
  ]);
  const [responseStatus, setResponseStatus] = useState(null);
  const [showResponse, setShowResponse] = useState(false);
  const [httpMethod, setHttpMethod] = useState("POST");
  const [contentType, setContentType] = useState("application/json");

  // Reset fields when switching between edit modes
  useEffect(() => {
    if (isEdit && value?.url) {
      setBody(value.url);
    }
  }, [isEdit, value]);

  const curlExample = useMemo(() => {
    const data = {
      phoneNumber,
      profileName,
      keyword,
      ...optionalFields.reduce((acc, field) => {
        if (field.key && field.value) {
          acc[field.key] = field.value;
        }
        return acc;
      }, {}),
    };

    let curlCommand = `curl -X ${httpMethod} "${
      body || API_ENDPOINT
    }/message/consume" \\\n`;
    curlCommand += `  -H "Content-Type: ${contentType}" \\\n`;

    if (contentType === "application/json") {
      curlCommand += `  -d '${JSON.stringify(data, null, 2)}'`;
    } else {
      // For form data
      const formData = Object.entries(data)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join("&");
      curlCommand += `  -d '${formData}'`;
    }

    return curlCommand;
  }, [
    body,
    phoneNumber,
    profileName,
    keyword,
    optionalFields,
    httpMethod,
    contentType,
  ]);

  const handleSubmit = async () => {
    if (!body) {
      toast.error(`Endpoint is required`);
      return;
    }
    if (!isValidUrl(body)) {
      toast.error(`Please enter valid endpoint!`);
      return;
    }
    if (!reply && !isEdit && !preview) {
      toast.error(`Reply text is required!`);
      return;
    }
    const payloadObject = { type: "endpoint", url: body };
    if (isEdit) {
      payloadObject.id = editId;
      payloadObject.isEdit = true;
    } else {
      payloadObject.triggerText = reply;
    }

    if (preview) {
      onUpdate?.({ type: "endpoint", url: body });
      return;
    }
    try {
      setSubmitting(true);
      const response = await axios.post(
        `${API_ENDPOINT}/message/save-message`,
        payloadObject,
        getHeader()
      );
      toast.success(response.data.message);
      resetFields();
    } catch (error) {
      toastError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyResponse = async () => {
    if (!body) {
      toast.error(`Endpoint is required`);
      return;
    }
    if (!isValidUrl(body)) {
      toast.error(`Please enter valid endpoint!`);
      return;
    }
    if (!phoneNumber || !profileName || !keyword) {
      toast.error(`Phone number, profile name, and keyword are required!`);
      return;
    }

    const payload = {
      phoneNumber,
      profileName,
      keyword,
      ...optionalFields.reduce((acc, field) => {
        if (field.key && field.value) {
          acc[field.key] = field.value;
        }
        return acc;
      }, {}),
    };

    const headers = {
      ...getHeader(),
      "Content-Type": contentType,
    };

    try {
      setVerifying(true);
      setResponseStatus("loading");
      const response = await axios({
        method: httpMethod.toLowerCase(),
        url: body,
        data:
          contentType === "application/json"
            ? payload
            : new URLSearchParams(payload),
        headers,
      });

      setResponseData(response.data);
      setResponseStatus("success");
      setShowResponse(true);
      toast.success("Response verified successfully!");
    } catch (error) {
      setResponseData({
        error: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
      });
      setResponseStatus("error");
      setShowResponse(true);
      toastError(error);
    } finally {
      setVerifying(false);
    }
  };

  function resetFields() {
    setBody("");
    setReply("");
    setPhoneNumber("919876543210");
    setProfileName("ACME Demo");
    setKeyword("hello");
    setOptionalFields([{ key: "", value: "" }]);
    setResponseData(null);
    setResponseStatus(null);
    setShowResponse(false);
  }

  const isValidUrlCheck = useMemo(() => {
    return body && !isValidUrl(body);
  }, [body]);

  const handleCopyCurl = () => {
    navigator.clipboard.writeText(curlExample).then(() => {
      setCopiedCurl(true);
      setTimeout(() => setCopiedCurl(false), 2000);
    });
  };

  const handleAddField = () => {
    setOptionalFields([...optionalFields, { key: "", value: "" }]);
  };

  const handleFieldChange = (index, field, value) => {
    const newFields = [...optionalFields];
    newFields[index][field] = value;
    setOptionalFields(newFields);
  };

  const handleRemoveField = (index) => {
    if (optionalFields.length > 1) {
      setOptionalFields(optionalFields.filter((_, i) => i !== index));
    } else {
      // Reset the single field instead of removing it
      setOptionalFields([{ key: "", value: "" }]);
    }
  };

  const hasEmptyOptionalFields = useMemo(() => {
    return (
      optionalFields.some((field) => field.key && !field.value) ||
      optionalFields.some((field) => !field.key && field.value)
    );
  }, [optionalFields]);

  return (
    <Box px={3}>
      <PageTitle>
        Endpoint Message{" "}
        {isEdit && (
          <Chip label="Edit Mode" size="small" color="primary" sx={{ ml: 1 }} />
        )}
      </PageTitle>

      <Alert
        severity="info"
        sx={{ mb: 2, borderRadius: 2 }}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => setOpenGuide(true)}
          >
            GUIDE
          </Button>
        }
      >
        This endpoint accepts <strong>{httpMethod}</strong> requests with&nbsp;
        <code>phoneNumber</code>, <code>profileName</code>, and{" "}
        <code>keyword</code>
        &nbsp;in the request body.
      </Alert>

      {/* Form */}
      <Box my={2}>
        <TextField
          fullWidth
          sx={{ my: 2 }}
          error={isValidUrlCheck}
          helperText={
            isValidUrlCheck
              ? "Please enter a valid URL"
              : "Enter your endpoint URL"
          }
          label="Callback URL"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          variant="outlined"
          size="small"
          placeholder="https://api.example.com/webhook"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Tooltip title="HTTP Method">
                  <FormControl variant="standard" sx={{ minWidth: 90 }}>
                    <Select
                      value={httpMethod}
                      onChange={(e) => setHttpMethod(e.target.value)}
                      label="Method"
                      size="small"
                    >
                      <MenuItem value="POST">POST</MenuItem>
                      <MenuItem value="GET">GET</MenuItem>
                      <MenuItem value="PUT">PUT</MenuItem>
                    </Select>
                  </FormControl>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />

        {!preview && (
          <TextField
            fullWidth
            sx={{ my: 2 }}
            label="Reply text"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            variant="outlined"
            size="small"
            helperText="This text will be shown as a placeholder while the endpoint is processing"
          />
        )}

        <Card variant="outlined" sx={{ my: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              Request Parameters
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  variant="outlined"
                  size="small"
                  required
                  helperText="Include country code"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Profile Name"
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  variant="outlined"
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Keyword"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  variant="outlined"
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Tooltip title="Content type for the request">
                  <FormControl fullWidth size="small">
                    <InputLabel>Content Type</InputLabel>
                    <Select
                      value={contentType}
                      label="Content Type"
                      onChange={(e) => setContentType(e.target.value)}
                    >
                      <MenuItem value="application/json">
                        application/json
                      </MenuItem>
                      <MenuItem value="application/x-www-form-urlencoded">
                        application/x-www-form-urlencoded
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Tooltip>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }}>
                  <Chip label="Optional Fields" size="small" />
                </Divider>
              </Grid>

              {optionalFields.map((field, index) => (
                <Grid item xs={12} key={index}>
                  <Box display="flex" gap={1} alignItems="flex-start">
                    <TextField
                      fullWidth
                      label="Key"
                      value={field.key}
                      onChange={(e) =>
                        handleFieldChange(index, "key", e.target.value)
                      }
                      variant="outlined"
                      size="small"
                      error={field.key && !field.value}
                    />
                    <TextField
                      fullWidth
                      label="Value"
                      value={field.value}
                      onChange={(e) =>
                        handleFieldChange(index, "value", e.target.value)
                      }
                      variant="outlined"
                      size="small"
                      error={field.value && !field.key}
                    />
                    <IconButton
                      color="error"
                      onClick={() => handleRemoveField(index)}
                      sx={{ mt: 0.5 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Grid>
              ))}

              {hasEmptyOptionalFields && (
                <Grid item xs={12}>
                  <Alert
                    severity="warning"
                    sx={{ py: 0 }}
                    icon={<ErrorIcon fontSize="small" />}
                  >
                    Optional fields should have both key and value filled or
                    left empty
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <Button
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={handleAddField}
                  variant="outlined"
                  size="small"
                >
                  Add Optional Field
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box display="flex" gap={2} my={2} flexWrap="wrap">
          <SaveButton
            loading={submitting}
            onClick={handleSubmit}
            disabled={isValidUrlCheck || hasEmptyOptionalFields}
          />

          <LoadingButton
            loading={verifying}
            variant="outlined"
            onClick={handleVerifyResponse}
            disabled={isValidUrlCheck || hasEmptyOptionalFields}
            startIcon={
              responseStatus === "success" ? <CheckCircleIcon /> : <SaveIcon />
            }
            color={responseStatus === "success" ? "success" : "primary"}
          >
            {responseStatus === "success" ? "Verified" : "Verify Response"}
          </LoadingButton>

          {responseData && (
            <Button
              variant="text"
              onClick={() => setShowResponse(!showResponse)}
              endIcon={showResponse ? <CloseIcon /> : <InfoOutlinedIcon />}
            >
              {showResponse ? "Hide" : "Show"} Response
            </Button>
          )}
        </Box>
      </Box>

      {/* Response Preview */}
      <Collapse in={showResponse && responseData}>
        <Box my={2}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Typography variant="subtitle2">
              API Response{" "}
              {responseStatus === "success" ? (
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Success"
                  color="success"
                  size="small"
                />
              ) : (
                <Chip
                  icon={<ErrorIcon />}
                  label="Error"
                  color="error"
                  size="small"
                />
              )}
            </Typography>
          </Box>
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
            <SyntaxHighlighter
              language="json"
              style={materialDark}
              customStyle={{ borderRadius: 8, fontSize: 14 }}
            >
              {JSON.stringify(responseData, null, 2)}
            </SyntaxHighlighter>
          </Paper>
        </Box>
      </Collapse>

      {/* Full guideline dialog */}
      <Dialog
        open={openGuide}
        onClose={() => setOpenGuide(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
          <InfoOutlinedIcon sx={{ mr: 1 }} />
          API Endpoint Usage Guide
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" paragraph>
            Your endpoint should be able to handle incoming webhook requests
            with the following structure:
          </Typography>

          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>
              Method: <Chip label={httpMethod} size="small" color="primary" />
            </Typography>

            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Headers:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mt: 0 }}>
              <li>
                <code>Content-Type: {contentType}</code>
              </li>
            </Box>

            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Required Parameters:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mt: 0 }}>
              <li>
                <code>phoneNumber</code> <em>(string)</em> – include country
                code, e.g., <code>919876543210</code>
              </li>
              <li>
                <code>profileName</code> <em>(string)</em> – WhatsApp profile
                display name
              </li>
              <li>
                <code>keyword</code> <em>(string)</em> – trigger keyword
              </li>
            </Box>

            <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
              Optional Parameters:
            </Typography>
            <Typography variant="body2" paragraph>
              You can add any additional key-value pairs as needed for your
              implementation.
            </Typography>
          </Box>

          <Typography variant="subtitle2" gutterBottom>
            Example Request (cURL):
          </Typography>
          <Box position="relative" sx={{ mb: 2 }}>
            <SyntaxHighlighter
              language="bash"
              style={materialDark}
              customStyle={{ borderRadius: 8, fontSize: 14 }}
            >
              {curlExample}
            </SyntaxHighlighter>
            <Tooltip title={copiedCurl ? "Copied!" : "Copy to clipboard"}>
              <IconButton
                size="small"
                onClick={handleCopyCurl}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: "rgba(0,0,0,0.7)",
                }}
                color="inherit"
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Alert severity="info" icon={<InfoOutlinedIcon />}>
            Your endpoint should return a valid HTTP response (status 200 for
            success). The response body will be sent back to the user.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenGuide(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EndpointMessage;
